// Lightweight first-party analytics for blog posts: views, reading-time/engagement, and clicks.
// Fires directly against the backend's public tracking endpoints — anonymous, no auth required.
// Mirrors the equivalent module in the map_my_pay (Next.js) app so both frontends feed the same
// admin-side analytics with the same visitor/session/event contract.

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:1111";
const PLATFORM = "map_my_pay_app";

const VISITOR_ID_KEY = "mmp_blog_visitor_id";
const SESSION_ID_KEY = "mmp_blog_session_id";

function generateId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

// Long-lived anonymous visitor id — survives across sessions/days (localStorage).
export function getVisitorId() {
  try {
    let id = window.localStorage.getItem(VISITOR_ID_KEY);
    if (!id) {
      id = generateId();
      window.localStorage.setItem(VISITOR_ID_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

// Per-tab-session id — resets each browser session (sessionStorage).
export function getSessionId() {
  try {
    let id = window.sessionStorage.getItem(SESSION_ID_KEY);
    if (!id) {
      id = generateId();
      window.sessionStorage.setItem(SESSION_ID_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

function hasAlreadyViewed(slug) {
  try {
    return window.sessionStorage.getItem(`mmp_blog_viewed_${slug}`) === "1";
  } catch {
    return false;
  }
}

function markViewed(slug) {
  try {
    window.sessionStorage.setItem(`mmp_blog_viewed_${slug}`, "1");
  } catch {
    // ignore
  }
}

function trackingUrl(slug, action) {
  return `${BASE_URL.replace(/\/$/, "")}/public/blogs/${encodeURIComponent(slug)}/${action}`;
}

function postJson(url, body) {
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    keepalive: true,
  }).catch(() => {
    // Analytics must never break the reading experience — swallow errors.
  });
}

function sendBeaconOrFetch(url, body) {
  const payload = JSON.stringify(body);
  if (typeof navigator !== "undefined" && navigator.sendBeacon) {
    const blob = new Blob([payload], { type: "application/json" });
    const sent = navigator.sendBeacon(url, blob);
    if (sent) return;
  }
  postJson(url, body);
}

// Fires once per session per blog post.
export function trackBlogView(slug) {
  if (!slug || hasAlreadyViewed(slug)) return;
  markViewed(slug);

  const params = new URLSearchParams(window.location.search);

  postJson(trackingUrl(slug, "view"), {
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    platform: PLATFORM,
    referrer_url: document.referrer || undefined,
    utm_source: params.get("utm_source") || undefined,
    utm_medium: params.get("utm_medium") || undefined,
    utm_campaign: params.get("utm_campaign") || undefined,
    utm_content: params.get("utm_content") || undefined,
    utm_term: params.get("utm_term") || undefined,
  });
}

// Fired once via sendBeacon when the reader leaves/hides the tab, with the accumulated active
// reading time and the deepest scroll position reached.
export function trackBlogEngagement(slug, { readingTimeMs, scrollDepthPercent }) {
  if (!slug) return;
  sendBeaconOrFetch(trackingUrl(slug, "engagement"), {
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    platform: PLATFORM,
    reading_time_ms: Math.max(0, Math.round(readingTimeMs)),
    scroll_depth_percent: Math.min(100, Math.max(0, Math.round(scrollDepthPercent))),
  });
}

// Fired for a meaningful click inside/around a blog post (share button, outbound link, CTA).
export function trackBlogClick(slug, clickTarget) {
  if (!slug || !clickTarget) return;
  postJson(trackingUrl(slug, "click"), {
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    platform: PLATFORM,
    click_target: clickTarget.slice(0, 100),
  });
}
